import { useEffect, useRef } from "react";
import { useFormikContext } from "formik";

import { type DealOpportunityDataPoint } from "@vega-api/generated/types/dealOpportunityDataPoint";
import { type DependentOption } from "@vega-api/generated/types/dependentOption";
import { ThemedDropdownField } from "@vega-core/components/Form/Controls/Dropdown/Dropdown/DropdownField";
import { type OptionType } from "@vega-core/models/options";

type FormValues = Record<string, string | undefined>;

export const DependentOptionsField = ({
  dataPoint,
  disabled = false,
}: {
  dataPoint: DealOpportunityDataPoint;
  disabled?: boolean;
}) => {
  const isFirstRender = useRef(true);

  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { name, displayName, placeholder, dependentField, dependentOptions } =
    dataPoint;

  const dependentFieldValue = values[dependentField ?? ""];

  useEffect(
    function clearDependentFieldOnParentChange() {
      // Skip the first render to avoid clearing the field when the component is first mounted
      if (isFirstRender.current) {
        isFirstRender.current = false;

        return;
      }

      setFieldValue(name, undefined);
    },
    [dependentFieldValue],
  );

  const matchingOptions: DependentOption | undefined = dependentOptions?.find(
    (opt) => opt.name === dependentFieldValue,
  );
  const dropdownOptions: OptionType[] = matchingOptions
    ? matchingOptions.options.map((option) => ({
        label: option,
        value: option,
      }))
    : [];

  return (
    <ThemedDropdownField
      disabled={disabled || !dependentFieldValue}
      isClearable={false}
      label={displayName}
      name={name}
      options={dropdownOptions}
      placeholder={placeholder}
    />
  );
};
